import * as languageMap from "shared/languageMap";
import BookStore from "stores/BookStore";

// Columns the library table can be sorted by (by clicking their header).
// "lastRead" has no column of its own; it's the implicit default order for
// non-shelf lists (most recently read first).
export type SortColumn =
  | "title"
  | "contentType"
  | "difficulty"
  | "permissions"
  | "owner"
  | "length"
  | "readStatus"
  | "lastRead";

const bookLength = (book: BookStore) => {
  const bookJSON = book.book.value;
  return (
    (languageMap.usesCharacterCount(bookJSON.language)
      ? bookJSON.metrics?.characters
      : bookJSON.metrics?.words) ?? 0
  );
};

// Never-read texts have no lastReadDate, so they sort as oldest.
const lastReadTime = (book: BookStore) => {
  const lastReadDate = book.book.value.userBook?.lastReadDate;
  return lastReadDate ? new Date(lastReadDate).getTime() : 0;
};

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// A compact relative label for the "Last Read" column: "just now", "4h ago",
// "8d ago", then "May 2025" once it's older than a month. Returns "" for
// never-read texts (no lastReadDate). `now` is injectable for testing.
export const formatLastRead = (
  lastReadDate: string | Date | undefined,
  now: Date = new Date()
): string => {
  if (!lastReadDate) return "";
  const then = new Date(lastReadDate);
  const elapsed = now.getTime() - then.getTime();
  if (elapsed < MINUTE) return "just now";
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)}m ago`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)}h ago`;
  if (elapsed < 30 * DAY) return `${Math.floor(elapsed / DAY)}d ago`;
  return then.toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });
};

const contentTypeRank = (book: BookStore) => {
  const bookJSON = book.book.value;
  if (bookJSON.youTubeID) return "video";
  if (bookJSON.audioFileID) return "audio";
  return "text";
};

// Difficulty orders by the numeric CEF level (A1=1 … C2=6); texts without a
// graded level sort as easiest.
const difficultyRank = (book: BookStore) =>
  Number(book.book.value.metrics?.CEFNumber) || 0;

// Share status, least-shared first: private (0) → unlisted/link-only (1) →
// public (2). Mirrors the lock / unlock / globe icons in the column.
const permissionRank = (book: BookStore) => {
  const bookJSON = book.book.value;
  if (!bookJSON.public) return 0;
  return bookJSON.hide ? 1 : 2;
};

const ownerName = (book: BookStore) => {
  const ownerID = book.book.value.ownerID;
  if (!ownerID || typeof ownerID === "string") return "";
  return (ownerID.shortUsername || ownerID.username || "").toLocaleLowerCase();
};

const bookTitle = (book: BookStore) =>
  (
    book.book.value.shortTitle ||
    book.book.value.title ||
    ""
  ).toLocaleLowerCase();

// Reading progress as a fraction of the text. This orders rows the same way as
// the displayed "Read X%". The percentage shown is computed in bookViewJSON from
// userBook.readTo (falling back to position) over totalWords, not stored on the
// raw book, so we derive the same measure here. A text marked completed counts
// as fully read, matching its full bar, even if it was never read to the end.
const readProgress = (book: BookStore) => {
  if (book.hasTag("completed")) return 1;
  const bookJSON = book.book.value;
  const userBook = bookJSON.userBook;
  const totalWords = Number(bookJSON.totalWords);
  if (!userBook || !totalWords) return 0;
  const raw =
    userBook.readTo != null
      ? Number(userBook.readTo)
      : Number(userBook.position ?? 0);
  return raw / totalWords;
};

const comparators: Record<SortColumn, (a: BookStore, b: BookStore) => number> =
  {
    title: (a, b) => bookTitle(a).localeCompare(bookTitle(b)),
    contentType: (a, b) => contentTypeRank(a).localeCompare(contentTypeRank(b)),
    difficulty: (a, b) => difficultyRank(a) - difficultyRank(b),
    permissions: (a, b) => permissionRank(a) - permissionRank(b),
    owner: (a, b) => ownerName(a).localeCompare(ownerName(b)),
    length: (a, b) => bookLength(a) - bookLength(b),
    readStatus: (a, b) => readProgress(a) - readProgress(b),
    lastRead: (a, b) => lastReadTime(a) - lastReadTime(b),
  };

// Returns a new sorted array. A null column means "no sort"; the caller's
// existing order (shelf order, or the server's last-read order) is preserved.
export const sortBooks = (
  books: BookStore[],
  column: SortColumn | null,
  ascending: boolean
): BookStore[] => {
  if (!column) return books;
  const compare = comparators[column];
  const direction = ascending ? 1 : -1;
  return [...books].sort((a, b) => direction * compare(a, b));
};
